import React, { useEffect, useState, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { TrendingUp, TrendingDown, AlertTriangle, DollarSign, Users, Wallet, Calendar, BarChart3, MapPin, FileText, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

import { API, blobErrorText } from '@/utils/api';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// Growth badge component - moved outside to avoid re-renders
const GrowthBadge = ({ value }) => {
  const val = value || 0;
  const isPositive = val >= 0;
  return (
    <span className={`inline-flex items-center gap-1 text-sm font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
      {isPositive ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
      {isPositive ? '+' : ''}{val.toFixed(1)}%
    </span>
  );
};

export default function Dashboard() {
  const { user, selectedDivision } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  // Which scope the breakdown sections (region-wise, etc.) reflect.
  // Click either KPI card to toggle. Defaults to 'month' so the
  // dashboard answers 'what's happening THIS month' first; the FY
  // view is one click away.
  const [breakdownScope, setBreakdownScope] = useState('month');

  // Which aging bucket the customer list reflects. Click any of the
  // three Overdue KPI cards to switch. Default '150_plus' preserves
  // the historical admin view ('only critical 150+ days shows up').
  // Buckets are DISJOINT so each customer appears once:
  //   90_plus  → max_days in [91, 119]
  //   120_plus → max_days in [120, 149]
  //   150_plus → max_days >= 150
  const [overdueScope, setOverdueScope] = useState('150_plus');
  // Blocked accounts are blocked for overdues — recovering from them is the
  // priority, so the overdue list can be narrowed to just those.
  const [blockedOnly, setBlockedOnly] = useState(false);
  const [blockedList, setBlockedList] = useState(null);   // { accounts, count, net_outstanding }
  const [blockedLoading, setBlockedLoading] = useState(false);

  // Drill-down: click a customer tile in the overdue list to see invoice-level
  // detail right here (sourced from /analytics/ageing) instead of digging
  // through the ledger. Ageing data is fetched lazily and cached per division.
  const [drillCustomer, setDrillCustomer] = useState(null);
  const [ageingDetail, setAgeingDetail] = useState(null);
  const [ageingDetailLoading, setAgeingDetailLoading] = useState(false);
  const [stmtBusy, setStmtBusy] = useState(false);

  const fetchDashboardData = useCallback(async () => {
    try {
      setAgeingDetail(null); // division changed (or refresh) — drop the cache
      setBlockedList(null);
      const params = selectedDivision && selectedDivision !== 'all' ? `?division=${encodeURIComponent(selectedDivision)}` : '';
      const response = await axios.get(`${API}/dashboard/summary${params}`, {
        withCredentials: true
      });
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [selectedDivision]);

  // Every blocked account in scope, regardless of overdue bucket — a block is
  // a profile-level fact, so the recovery list must not depend on which
  // bucket card is selected. Fetched when the toggle is first switched on.
  const fetchBlockedList = async () => {
    setBlockedLoading(true);
    try {
      const params = selectedDivision && selectedDivision !== 'all' ? { division: selectedDivision } : {};
      const res = await axios.get(`${API}/analytics/blocked-accounts`, { params, withCredentials: true });
      setBlockedList(res.data);
    } catch (err) {
      console.error('Failed to fetch blocked accounts:', err);
      toast.error('Could not load the blocked accounts list.');
    } finally {
      setBlockedLoading(false);
    }
  };
  useEffect(() => {
    fetchBlockedList();  // eager: the toggle shows the count, and the section renders even with nothing overdue
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDivision]);

  const openCustomerDrill = async (customer) => {
    setDrillCustomer(customer);
    if (ageingDetail) return;
    setAgeingDetailLoading(true);
    try {
      const params = selectedDivision && selectedDivision !== 'all' ? { division: selectedDivision } : {};
      const res = await axios.get(`${API}/analytics/ageing`, { params, withCredentials: true });
      setAgeingDetail(res.data);
    } catch (err) {
      console.error('Failed to fetch ageing detail:', err);
      toast.error('Could not load invoice-level detail.');
    } finally {
      setAgeingDetailLoading(false);
    }
  };

  const downloadDrillStatement = async (fmt = 'pdf') => {
    if (!drillCustomer?.customer_id) return;
    setStmtBusy(true);
    try {
      const params = { format: fmt };
      if (selectedDivision && selectedDivision !== 'all') params.division = selectedDivision;
      const res = await axios.get(`${API}/customers/${drillCustomer.customer_id}/account-statement`, {
        params, withCredentials: true, responseType: 'blob', timeout: 120000,
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const safeName = (drillCustomer.customer_name || 'Customer').replace(/[^A-Za-z0-9_-]+/g, '_').slice(0, 30);
      link.setAttribute('download', `Statement_${safeName}.${fmt === 'excel' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Statement downloaded');
    } catch (err) {
      toast.error(`Statement download failed: ${(await blobErrorText(err)) || 'unknown error'}`);
    } finally {
      setStmtBusy(false);
    }
  };
  
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!data) {
    return (
      <div className="p-8">
        <p className="text-muted-foreground">No data available</p>
      </div>
    );
  }

  const formatCurrency = (value) => {
    const v = value || 0;
    if (v >= 10000000) {
      return `₹${(v / 10000000).toFixed(2)} Cr`;
    } else if (v >= 100000) {
      return `₹${(v / 100000).toFixed(2)} L`;
    } else if (v >= 1000) {
      return `₹${(v / 1000).toFixed(1)} K`;
    }
    return `₹${v.toLocaleString()}`;
  };
  
  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-heading font-bold text-foreground mb-1">
          Dashboard
        </h1>
        <p className="text-muted-foreground text-sm">
          {MONTH_NAMES[(data.current_month || 1) - 1]} {new Date().getFullYear()} Overview ({data.fy_label || `FY ${data.current_year || new Date().getFullYear()}`}) • Welcome, {user?.name}
        </p>
      </div>
      
      {/* Sales Section — KPI cards double as scope toggles for the
          breakdown sections below. Active card gets a coloured ring +
          background tint so it's obvious which scope drives the data
          being shown. */}
      <div className="mb-6">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3 flex items-center justify-between">
          <span>Sales Performance</span>
          <span className="text-[10px] font-normal text-muted-foreground normal-case tracking-normal">
            Click a card to filter breakdowns below
          </span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Monthly Sales — clickable toggle */}
          <button
            type="button"
            onClick={() => setBreakdownScope('month')}
            className={`text-left transition-all rounded-lg ${
              breakdownScope === 'month'
                ? 'ring-2 ring-indigo-500 ring-offset-2 ring-offset-background'
                : 'ring-0 hover:ring-1 hover:ring-indigo-200'
            }`}
            aria-pressed={breakdownScope === 'month'}
          >
            <Card className={`border-border/60 shadow-sm ${breakdownScope === 'month' ? 'bg-indigo-50/30' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1.5">
                      Sales This Month
                      {breakdownScope === 'month' && (
                        <span className="text-[9px] uppercase font-semibold text-indigo-600 bg-indigo-100 px-1.5 py-0.5 rounded">Active</span>
                      )}
                    </p>
                    <p className="text-2xl font-bold text-foreground">{formatCurrency(data.total_sales_month)}</p>
                    <div className="mt-1">
                      <GrowthBadge value={data.mom_growth} />
                      <span className="text-xs text-muted-foreground ml-1">vs last month</span>
                    </div>
                  </div>
                  <div className="bg-indigo-50 text-indigo-600 p-2 rounded-lg">
                    <Calendar size={20} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </button>

          {/* Last Month Sales — clickable toggle. Useful at month-start when
              the current month barely has data but last month just closed. */}
          <button
            type="button"
            onClick={() => setBreakdownScope('last_month')}
            className={`text-left transition-all rounded-lg ${
              breakdownScope === 'last_month'
                ? 'ring-2 ring-sky-500 ring-offset-2 ring-offset-background'
                : 'ring-0 hover:ring-1 hover:ring-sky-200'
            }`}
            aria-pressed={breakdownScope === 'last_month'}
          >
            <Card className={`border-border/60 shadow-sm ${breakdownScope === 'last_month' ? 'bg-sky-50/30' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1.5">
                      Sales Last Month{data.prev_month ? ` (${MONTH_NAMES[data.prev_month - 1]})` : ''}
                      {breakdownScope === 'last_month' && (
                        <span className="text-[9px] uppercase font-semibold text-sky-600 bg-sky-100 px-1.5 py-0.5 rounded">Active</span>
                      )}
                    </p>
                    <p className="text-2xl font-bold text-foreground">{formatCurrency(data.total_sales_last_month)}</p>
                    <div className="mt-1">
                      <GrowthBadge value={data.last_month_growth} />
                      <span className="text-xs text-muted-foreground ml-1">vs prior month</span>
                    </div>
                  </div>
                  <div className="bg-sky-50 text-sky-600 p-2 rounded-lg">
                    <Calendar size={20} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </button>

          {/* Yearly Sales — clickable toggle */}
          <button
            type="button"
            onClick={() => setBreakdownScope('fy')}
            className={`text-left transition-all rounded-lg ${
              breakdownScope === 'fy'
                ? 'ring-2 ring-purple-500 ring-offset-2 ring-offset-background'
                : 'ring-0 hover:ring-1 hover:ring-purple-200'
            }`}
            aria-pressed={breakdownScope === 'fy'}
          >
            <Card className={`border-border/60 shadow-sm ${breakdownScope === 'fy' ? 'bg-purple-50/30' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1.5">
                      Sales This FY ({data.fy_label || ''})
                      {breakdownScope === 'fy' && (
                        <span className="text-[9px] uppercase font-semibold text-purple-600 bg-purple-100 px-1.5 py-0.5 rounded">Active</span>
                      )}
                    </p>
                    <p className="text-2xl font-bold text-foreground">{formatCurrency(data.total_sales_year)}</p>
                    <div className="mt-1">
                      <GrowthBadge value={data.yoy_growth} />
                      <span className="text-xs text-muted-foreground ml-1">vs last FY (like-to-like)</span>
                    </div>
                  </div>
                  <div className="bg-purple-50 text-purple-600 p-2 rounded-lg">
                    <BarChart3 size={20} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </button>
        </div>
      </div>

      {/* Sales by Region — switches between FY and Month rollups
          based on which KPI card is active. */}
      {(() => {
        // Pick the right rollup for the active scope. Fall back to the FY
        // list ONLY when the month field is MISSING entirely (older API
        // response) — an empty month list means "no sales this month yet"
        // and must NOT silently show FY numbers under a month heading.
        const rollup = breakdownScope === 'month'
          ? (data.sales_by_region_month ?? data.sales_by_region)
          : breakdownScope === 'last_month'
            ? (data.sales_by_region_last_month || [])
            : data.sales_by_region;
        const totalSales = (rollup || []).reduce((sum, x) => sum + x.amount, 0);
        const maxAmount = rollup?.[0]?.amount || 1;
        // Heading label updates with scope so it's clear which window
        // the numbers reflect.
        const monthLabel = `${MONTH_NAMES[(data.current_month || 1) - 1]} ${new Date().getFullYear()}`;
        const lastMonthLabel = data.prev_month
          ? `${MONTH_NAMES[data.prev_month - 1]} ${data.prev_month_year || new Date().getFullYear()}`
          : 'Last Month';
        const scopeLabel = breakdownScope === 'month'
          ? monthLabel
          : breakdownScope === 'last_month'
            ? lastMonthLabel
            : (data.fy_label || '');
        // Empty scope (e.g. a new month before the first import): show an
        // honest empty state under the correct heading instead of nothing —
        // and never another scope's numbers.
        if (!rollup?.length) {
          return (
            <div className="mb-6">
              <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
                <MapPin size={14} />
                Region-wise Breakdown ({scopeLabel})
              </h2>
              <div className="bg-white rounded-lg border border-border/60 shadow-sm px-4 py-6 text-sm text-muted-foreground">
                No sales recorded for {scopeLabel} yet. Click the FY card above to see the year-to-date breakdown.
              </div>
            </div>
          );
        }
        return (
          <div className="mb-6">
            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
              <MapPin size={14} />
              Region-wise Breakdown ({scopeLabel})
            </h2>
            <div className="bg-white rounded-lg border border-border/60 shadow-sm overflow-hidden">
              {/* Table header */}
              <div className="grid grid-cols-12 gap-2 px-4 py-2.5 bg-gray-50 border-b text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                <div className="col-span-3">Region</div>
                <div className="col-span-5">Sales</div>
                <div className="col-span-2 text-right">Amount</div>
                <div className="col-span-2 text-center">Customers</div>
              </div>
              {/* Table rows */}
              {rollup.map((r, i) => {
                const pct = totalSales > 0 ? ((r.amount / totalSales) * 100) : 0;
                const barColors = [
                  'bg-indigo-500', 'bg-violet-500', 'bg-blue-500', 'bg-cyan-500',
                  'bg-teal-500', 'bg-emerald-500', 'bg-amber-500', 'bg-rose-500',
                ];
                const barColor = barColors[i % barColors.length];
                return (
                  <div key={i} className={`grid grid-cols-12 gap-2 px-4 py-3 items-center ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} ${i < rollup.length - 1 ? 'border-b border-gray-100' : ''}`}>
                    <div className="col-span-3 text-sm font-medium text-foreground truncate" title={r.name}>
                      {r.name}
                    </div>
                    <div className="col-span-5 flex items-center gap-2">
                      <div className="flex-1 bg-gray-100 rounded-full h-2.5 overflow-hidden">
                        <div
                          className={`${barColor} h-2.5 rounded-full transition-all duration-500`}
                          style={{ width: `${Math.max(3, (r.amount / maxAmount) * 100)}%` }}
                        />
                      </div>
                      <span className="text-[11px] text-muted-foreground w-10 text-right">{pct.toFixed(0)}%</span>
                    </div>
                    <div className="col-span-2 text-sm font-semibold text-foreground text-right">
                      {formatCurrency(r.amount)}
                    </div>
                    <div className="col-span-2 flex justify-center">
                      <span className="inline-flex items-center gap-1 text-xs font-medium bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full">
                        <Users size={10} />
                        {r.customers || 0}
                      </span>
                    </div>
                  </div>
                );
              })}
              {/* Footer totals */}
              <div className="grid grid-cols-12 gap-2 px-4 py-2.5 bg-gray-50 border-t text-sm font-semibold">
                <div className="col-span-3 text-muted-foreground">Total</div>
                <div className="col-span-5"></div>
                <div className="col-span-2 text-right text-foreground">{formatCurrency(totalSales)}</div>
                <div className="col-span-2 flex justify-center">
                  <span className="inline-flex items-center gap-1 text-xs font-semibold bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded-full">
                    <Users size={10} />
                    {breakdownScope === 'month'
                      ? (data.total_customers_billed_month || rollup.reduce((sum, x) => sum + (x.customers || 0), 0))
                      : breakdownScope === 'last_month'
                        ? (data.total_customers_billed_last_month || rollup.reduce((sum, x) => sum + (x.customers || 0), 0))
                        : rollup.reduce((sum, x) => sum + (x.customers || 0), 0)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Payments Section */}
      <div className="mb-6">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">Receivables</h2>
        <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
          {/* Total Amount Due */}
          <Card className="border-border/60 shadow-sm bg-gradient-to-br from-blue-50 to-white">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Total Amount Due</p>
                  <p className="text-2xl font-bold text-blue-600">{formatCurrency(data.total_amount_due)}</p>
                  <p className="text-xs text-muted-foreground mt-1">outstanding balance</p>
                </div>
                <div className="bg-blue-100 text-blue-600 p-2 rounded-lg">
                  <DollarSign size={20} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        {/* Amount Due by Region */}
        {data.amount_due_by_region?.length > 0 && (
          <Card className="border-border/60 shadow-sm mt-4">
            <CardContent className="p-4">
              <p className="text-xs font-medium text-muted-foreground mb-3">Amount Due by Region</p>
              <div className="space-y-3">
                {data.amount_due_by_region.map((r, i) => {
                  const maxDue = data.amount_due_by_region[0]?.amount || 1;
                  return (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-sm text-muted-foreground w-28 truncate" title={r.name}>{r.name}</span>
                      <div className="flex-1 bg-gray-100 rounded-full h-3">
                        <div
                          className="bg-red-500 h-3 rounded-full transition-all"
                          style={{ width: `${Math.max(2, (r.amount / maxDue) * 100)}%` }}
                        />
                      </div>
                      <span className="text-sm font-semibold text-red-600 whitespace-nowrap w-24 text-right">{formatCurrency(r.amount)}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Overdues Section — KPI cards toggle which aging bucket of
          customers is shown below. Active card gets a coloured ring +
          tint, same pattern as the Sales KPI toggle above. */}
      <div className="mb-6">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3 flex items-center justify-between">
          <span>Overdue Analysis</span>
          <span className="text-[10px] font-normal text-muted-foreground normal-case tracking-normal">
            Click a card to see customers in that bucket
          </span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Overdues 90+ Days — clickable */}
          <button
            type="button"
            onClick={() => setOverdueScope('90_plus')}
            className={`text-left transition-all rounded-lg ${
              overdueScope === '90_plus'
                ? 'ring-2 ring-orange-500 ring-offset-2 ring-offset-background'
                : 'ring-0 hover:ring-1 hover:ring-orange-200'
            }`}
            aria-pressed={overdueScope === '90_plus'}
          >
            <Card className="border-border/60 shadow-sm bg-gradient-to-br from-orange-50 to-white">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1.5">
                      Overdues &gt; 90 Days
                      {overdueScope === '90_plus' && (
                        <span className="text-[9px] uppercase font-semibold text-orange-600 bg-orange-100 px-1.5 py-0.5 rounded">Active</span>
                      )}
                    </p>
                    <p className="text-2xl font-bold text-orange-600">{formatCurrency(data.total_overdues_90_plus)}</p>
                    <p className="text-xs text-orange-600 font-medium mt-1">
                      {data.count_overdues_90_plus} invoices
                    </p>
                  </div>
                  <div className="bg-orange-100 text-orange-600 p-2 rounded-lg">
                    <AlertTriangle size={20} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </button>

          {/* Overdues 120+ Days — clickable */}
          <button
            type="button"
            onClick={() => setOverdueScope('120_plus')}
            className={`text-left transition-all rounded-lg ${
              overdueScope === '120_plus'
                ? 'ring-2 ring-red-500 ring-offset-2 ring-offset-background'
                : 'ring-0 hover:ring-1 hover:ring-red-200'
            }`}
            aria-pressed={overdueScope === '120_plus'}
          >
            <Card className="border-border/60 shadow-sm bg-gradient-to-br from-red-50 to-white">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1.5">
                      Overdues &gt; 120 Days
                      {overdueScope === '120_plus' && (
                        <span className="text-[9px] uppercase font-semibold text-red-600 bg-red-100 px-1.5 py-0.5 rounded">Active</span>
                      )}
                    </p>
                    <p className="text-2xl font-bold text-red-600">{formatCurrency(data.total_overdues_120_plus)}</p>
                    <p className="text-xs text-red-600 font-medium mt-1">
                      {data.count_overdues_120_plus} invoices
                    </p>
                  </div>
                  <div className="bg-red-100 text-red-600 p-2 rounded-lg">
                    <AlertTriangle size={20} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </button>

          {/* Overdues 150+ Days — clickable */}
          <button
            type="button"
            onClick={() => setOverdueScope('150_plus')}
            className={`text-left transition-all rounded-lg ${
              overdueScope === '150_plus'
                ? 'ring-2 ring-red-700 ring-offset-2 ring-offset-background'
                : 'ring-0 hover:ring-1 hover:ring-red-300'
            }`}
            aria-pressed={overdueScope === '150_plus'}
          >
            <Card className="border-border/60 shadow-sm bg-gradient-to-br from-red-100 to-red-50 border-red-200">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs text-red-700 font-medium mb-1 flex items-center gap-1.5">
                      CRITICAL &gt; 150 Days
                      {overdueScope === '150_plus' && (
                        <span className="text-[9px] uppercase font-semibold text-red-700 bg-red-200 px-1.5 py-0.5 rounded">Active</span>
                      )}
                    </p>
                    <p className="text-2xl font-bold text-red-700">{formatCurrency(data.total_overdues_150_plus)}</p>
                    <p className="text-xs text-red-600 font-medium mt-1">
                      {data.count_overdues_150_plus} invoices
                    </p>
                  </div>
                  <div className="bg-red-200 text-red-700 p-2 rounded-lg">
                    <AlertTriangle size={20} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </button>
        </div>
      </div>

      {/* Customer List — filtered to the active overdue bucket.
          The three Overdue KPI cards above act as the toggle.
          Buckets are DISJOINT (max_days range) so each customer
          appears in exactly one bucket regardless of which card
          is selected. */}
      {(() => {
        const all = data.critical_customers_90_plus || data.critical_customers_150_plus || [];
        if (!all.length && !blockedOnly && !(blockedList?.count > 0)) return null;

        // Disjoint bucketing based on max_days
        const inBucket = (c) => {
          const d = c.max_days || 0;
          if (overdueScope === '90_plus')  return d > 90  && d < 120;
          if (overdueScope === '120_plus') return d >= 120 && d < 150;
          if (overdueScope === '150_plus') return d >= 150;
          return false;
        };
        const customers = all.filter(inBucket).sort((a, b) => (b.max_days || 0) - (a.max_days || 0));
        const blockedAccounts = blockedList?.accounts || [];

        // Styling + label per scope
        const cfg = {
          '90_plus':  { color: 'orange', label: 'Customers 90-119 Days Overdue',
                        sub: 'Follow up required', tone: 'orange-600' },
          '120_plus': { color: 'red',    label: 'Customers 120-149 Days Overdue',
                        sub: 'Escalation needed',  tone: 'red-600' },
          '150_plus': { color: 'red',    label: 'Critical Customers — 150+ Days Overdue',
                        sub: user?.role === 'sales_member' ? 'Immediate action required' : 'Management attention required',
                        tone: 'red-700' },
        }[overdueScope];

        const cardBorder = cfg.color === 'orange' ? 'border-orange-200' : 'border-red-200';
        const cardBg     = cfg.color === 'orange' ? 'bg-orange-50/50'   : 'bg-red-50/50';
        const tileBorder = cfg.color === 'orange' ? 'border-orange-200' : 'border-red-200';
        const daysClr    = cfg.color === 'orange' ? 'text-orange-600'   : 'text-red-600';
        const amtClr     = cfg.color === 'orange' ? 'text-orange-700'   : 'text-red-700';
        const headingClr = `text-${cfg.tone}`;

        return (
          <div className="mb-6">
            <h2 className={`text-sm font-medium ${headingClr} uppercase tracking-wide mb-3 flex items-center gap-2`}>
              <AlertTriangle size={16} />
              {blockedOnly ? 'Blocked Accounts — all buckets' : cfg.label}
              <span className="text-[10px] text-muted-foreground normal-case tracking-normal ml-2">
                {blockedOnly
                  ? `· ${blockedAccounts.length} ${blockedAccounts.length === 1 ? 'account' : 'accounts'} · ${formatCurrency(blockedList?.net_outstanding || 0)} outstanding · recovery priority, biggest exposure first`
                  : `· ${customers.length} ${customers.length === 1 ? 'customer' : 'customers'} · ${cfg.sub} · click a customer for invoice-level detail`}
              </span>
              <button
                type="button"
                onClick={() => setBlockedOnly(v => !v)}
                aria-pressed={blockedOnly}
                title="Every account currently blocked on its profile, whatever its ageing bucket"
                className={`ml-auto normal-case tracking-normal text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                  blockedOnly
                    ? 'bg-red-600 border-red-600 text-white'
                    : 'bg-white border-red-300 text-red-700 hover:bg-red-50'
                }`}
              >
                {blockedOnly ? 'Back to buckets' : `Blocked accounts${blockedList ? ` · ${blockedList.count}` : ''}`}
              </button>
            </h2>
            <Card className={`${cardBorder} shadow-sm ${cardBg}`}>
              <CardContent className="p-4">
                {blockedOnly ? (
                  blockedLoading && !blockedList ? (
                    <p className="text-sm text-muted-foreground py-4 text-center">Loading blocked accounts…</p>
                  ) : blockedAccounts.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4 text-center">No accounts are blocked{selectedDivision && selectedDivision !== 'all' ? ' in your scope' : ''}.</p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {blockedAccounts.map((acc) => {
                        const since = acc.blocked_since ? new Date(acc.blocked_since).toLocaleDateString('en-IN') : null;
                        return (
                          <button
                            key={acc.customer_id}
                            type="button"
                            onClick={() => openCustomerDrill(acc)}
                            className="text-left bg-white rounded-lg p-3 border border-red-200 shadow-sm hover:shadow-md hover:ring-2 hover:ring-indigo-300 transition-all cursor-pointer"
                            title="Click for invoice-level detail"
                          >
                            <div className="flex items-start justify-between gap-1">
                              <p className="font-semibold text-foreground text-sm truncate" title={acc.customer_name}>
                                {acc.customer_name}
                              </p>
                              <ChevronRight size={14} className="text-muted-foreground shrink-0 mt-0.5" />
                            </div>
                            <p className="text-[11px] text-red-800 mt-0.5 truncate" title={acc.status_reason || ''}>
                              {acc.status_reason || 'No reason recorded'}{since ? ` · since ${since}` : ''}
                            </p>
                            <div className="flex items-center justify-between mt-1.5">
                              <span className={`text-xs font-medium ${acc.max_days > 90 ? 'text-red-600' : 'text-muted-foreground'}`}>
                                {acc.invoice_count ? `${acc.max_days} days oldest` : 'nothing open'}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {acc.invoice_count} inv{acc.region ? ` · ${acc.region}` : ''}
                              </span>
                            </div>
                            <p className="text-xs text-red-700 font-semibold mt-1">
                              {formatCurrency(acc.net_outstanding)} outstanding
                              {acc.overdue_90_plus > 0 && acc.overdue_90_plus < acc.net_outstanding && (
                                <span className="text-[10px] font-normal text-muted-foreground ml-1">· {formatCurrency(acc.overdue_90_plus)} over 90d</span>
                              )}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  )
                ) : customers.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">
                    No customers in this bucket — nothing to action right now.
                  </p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {customers.map((customer, idx) => {
                      // Show the amount belonging to THIS bucket, not the
                      // customer's whole >90d exposure (which previously made
                      // the 150+ column overstate tiles). Falls back to the
                      // old total when the API payload predates the slices.
                      const sliceAmt = overdueScope === '150_plus'
                        ? (customer.overdue_150_plus ?? customer.total_overdue)
                        : overdueScope === '120_plus'
                          ? (customer.overdue_120_149 ?? customer.total_overdue)
                          : (customer.overdue_91_119 ?? customer.total_overdue);
                      const sliceCnt = overdueScope === '150_plus'
                        ? (customer.count_150_plus ?? customer.invoice_count)
                        : overdueScope === '120_plus'
                          ? (customer.count_120_149 ?? customer.invoice_count)
                          : (customer.count_91_119 ?? customer.invoice_count);
                      const showTotal = (customer.total_overdue || 0) - (sliceAmt || 0) > 1;
                      return (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => openCustomerDrill(customer)}
                          className={`text-left bg-white rounded-lg p-3 border ${tileBorder} shadow-sm hover:shadow-md hover:ring-2 hover:ring-indigo-300 transition-all cursor-pointer`}
                          title={(user?.role === 'admin' || user?.role === 'accounts')
                            ? 'Click for invoice-level detail and statement'
                            : 'Click for invoice-level detail'}
                        >
                          <div className="flex items-start justify-between gap-1">
                            <p className="font-semibold text-foreground text-sm truncate" title={customer.customer_name}>
                              {customer.customer_name}
                            </p>
                            <ChevronRight size={14} className="text-muted-foreground shrink-0 mt-0.5" />
                          </div>
                          {customer.status && customer.status !== 'active' && (
                            <span
                              className={`inline-block mt-1 px-1.5 py-0.5 rounded border text-[9px] font-semibold uppercase tracking-wide leading-none ${
                                customer.status === 'blocked'
                                  ? 'bg-red-100 text-red-800 border-red-300'
                                  : customer.status === 'closed'
                                    ? 'bg-gray-200 text-gray-700 border-gray-300'
                                    : 'bg-amber-100 text-amber-800 border-amber-300'
                              }`}
                              title={customer.status_reason ? `${customer.status}: ${customer.status_reason}` : customer.status}
                            >
                              {customer.status}
                            </span>
                          )}
                          <div className="flex items-center justify-between mt-1">
                            <span className={`text-xs ${daysClr} font-medium`}>
                              {customer.max_days} days
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {sliceCnt} inv in bucket
                            </span>
                          </div>
                          <p className={`text-xs ${amtClr} font-semibold mt-1`}>
                            {formatCurrency(sliceAmt)}
                            {showTotal && (
                              <span className="text-[10px] font-normal text-muted-foreground ml-1">
                                · {formatCurrency(customer.total_overdue)} total 90+
                              </span>
                            )}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        );
      })()}

      {/* Customer drill-down — invoice-level detail for management review,
          sourced from /analytics/ageing, with one-click statement PDF. */}
      <Dialog open={!!drillCustomer} onOpenChange={(o) => { if (!o) setDrillCustomer(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle size={16} className="text-red-600" />
              {drillCustomer?.customer_name}
              {drillCustomer?.status && drillCustomer.status !== 'active' && (
                <span
                  className={`px-1.5 py-0.5 rounded border text-[10px] font-semibold uppercase tracking-wide ${drillCustomer.status === 'blocked' ? 'bg-red-100 text-red-800 border-red-300' : 'bg-amber-100 text-amber-800 border-amber-300'}`}
                  title={drillCustomer.status_reason || ''}
                >
                  {drillCustomer.status}{drillCustomer.status_reason ? ` — ${drillCustomer.status_reason}` : ''}
                </span>
              )}
              {drillCustomer?.customer_code && (
                <span className="text-xs font-normal text-muted-foreground">({drillCustomer.customer_code})</span>
              )}
            </DialogTitle>
            <DialogDescription>
              All open items by invoice age
              {drillCustomer?.region ? ` · ${drillCustomer.region}` : ''}
              {selectedDivision && selectedDivision !== 'all' ? ` · ${selectedDivision}` : ''}
              {' '}— rows in the selected overdue bucket are highlighted.
            </DialogDescription>
          </DialogHeader>
          {ageingDetailLoading ? (
            <div className="flex items-center justify-center py-10">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : (() => {
            const buckets = ['0-15', '16-45', '46-90', '91-120', '121+'];
            const rows = buckets
              .flatMap(b => (ageingDetail?.[b] || []))
              .filter(it => it.customer_id === drillCustomer?.customer_id)
              .sort((a, b) => (b.days_from_invoice || 0) - (a.days_from_invoice || 0));
            const inBucket = (d) => {
              if (overdueScope === '90_plus') return d > 90 && d < 120;
              if (overdueScope === '120_plus') return d >= 120 && d < 150;
              return d >= 150;
            };
            const openTotal = rows.reduce((s, r) => s + (r.amount || 0), 0);
            const bucketTotal = rows.filter(r => inBucket(r.days_from_invoice || 0)).reduce((s, r) => s + (r.amount || 0), 0);
            const credits = ageingDetail?.customer_credits?.[drillCustomer?.customer_id] || 0;
            const net = openTotal - credits;
            const bucketLabel = overdueScope === '90_plus' ? '90-119 days' : overdueScope === '120_plus' ? '120-149 days' : '150+ days';
            return (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <div className="p-2.5 bg-red-50 rounded-lg border border-red-200 text-center">
                    <p className="text-base font-bold text-red-700">{formatCurrency(bucketTotal)}</p>
                    <p className="text-[10px] text-muted-foreground">In bucket ({bucketLabel})</p>
                  </div>
                  <div className="p-2.5 bg-gray-50 rounded-lg border border-border/60 text-center">
                    <p className="text-base font-bold text-foreground">{formatCurrency(openTotal)}</p>
                    <p className="text-[10px] text-muted-foreground">Total aged debits</p>
                  </div>
                  <div className="p-2.5 bg-emerald-50 rounded-lg border border-emerald-200 text-center">
                    <p className="text-base font-bold text-emerald-700">− {formatCurrency(credits)}</p>
                    <p className="text-[10px] text-muted-foreground">Unadjusted credits</p>
                  </div>
                  <div className="p-2.5 bg-indigo-50 rounded-lg border border-indigo-200 text-center">
                    <p className="text-base font-bold text-indigo-700">{formatCurrency(net)}</p>
                    <p className="text-[10px] text-muted-foreground">Net position</p>
                  </div>
                </div>
                {rows.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">
                    No open aged debits found for this customer{ageingDetail ? '' : ' (detail still loading)'}.
                  </p>
                ) : (
                  <div className="overflow-x-auto border rounded-lg">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 border-b text-[11px] uppercase tracking-wide text-muted-foreground">
                          <th className="text-left p-2.5 font-medium">Invoice / Ref</th>
                          <th className="text-left p-2.5 font-medium">Date</th>
                          <th className="text-right p-2.5 font-medium">Days</th>
                          <th className="text-right p-2.5 font-medium">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r, i) => {
                          const hot = inBucket(r.days_from_invoice || 0);
                          return (
                            <tr key={i} className={`border-b last:border-b-0 ${hot ? 'bg-red-50/70' : ''}`}>
                              <td className="p-2.5">
                                <span className="font-medium">{r.invoice_number}</span>
                                {r.entry_type && r.entry_type !== 'invoice' && (
                                  <span className="inline-block ml-1.5 px-1.5 py-0.5 rounded border border-purple-300 bg-purple-100 text-purple-800 text-[9px] font-semibold leading-none whitespace-nowrap align-middle">
                                    {r.entry_type.replace(/_/g, ' ')}
                                  </span>
                                )}
                              </td>
                              <td className="p-2.5 text-muted-foreground">
                                {r.invoice_date ? new Date(r.invoice_date).toLocaleDateString('en-IN') : '—'}
                              </td>
                              <td className={`p-2.5 text-right font-semibold ${hot ? 'text-red-700' : (r.days_from_invoice > 90 ? 'text-orange-600' : 'text-foreground')}`}>
                                {r.days_from_invoice}
                              </td>
                              <td className="p-2.5 text-right font-semibold">
                                ₹{(r.amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                              </td>
                            </tr>
                          );
                        })}
                        <tr className="bg-gray-50 font-bold">
                          <td className="p-2.5" colSpan={3}>Total aged debits</td>
                          <td className="p-2.5 text-right">₹{openTotal.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
                <p className="text-[11px] text-muted-foreground">
                  Ageing by invoice date · balances are net of recorded payments/CD · unadjusted credits
                  cover payments, GR credit notes and other credit notes not yet adjusted against bills.
                </p>
              </div>
            );
          })()}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDrillCustomer(null)}>Close</Button>
            {/* Statements are customer-facing artefacts — admin/accounts only
                (backend enforces the same). */}
            {(user?.role === 'admin' || user?.role === 'accounts') && (
              <>
                <Button
                  variant="outline"
                  className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                  disabled={stmtBusy || !drillCustomer?.customer_id}
                  onClick={() => downloadDrillStatement('excel')}
                  title="Editable statement — remove line items before sharing with the customer"
                >
                  <FileText size={14} className="mr-1" />
                  {stmtBusy ? 'Building…' : 'Statement Excel'}
                </Button>
                <Button
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  disabled={stmtBusy || !drillCustomer?.customer_id}
                  onClick={() => downloadDrillStatement('pdf')}
                  title="Download the full Statement of Account (open items) for this customer"
                >
                  <FileText size={14} className="mr-1" />
                  {stmtBusy ? 'Building…' : 'Statement PDF'}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
